import {
  getNgieRowConfig,
  ngieGroups,
} from "../../../pages/group-data/utils/ngieUtils";
import {
  getPageTooltips,
  getSelectedPeriod,
} from "../../../services/GroupContext";
import {
  addGroupDataSubheaderCells,
  extractKeyValuePairsFromExcel,
  getWorksheetFromFile,
} from "./DataImportUtils";
import {
  addGroupDataWorksheet,
  downloadGroupDataImportTemplate,
  getGroupDataInitialSheetBuilder,
  validateImportedGroupData,
} from "./groupDataImportUtils";

const getTableRows = (group, isImportTemplate = true) => {
  const ngie = getSelectedPeriod()?.ngie;
  const rows = [];

  const tooltips = getPageTooltips("ngieModal");
  const ngieRowConfig = getNgieRowConfig(tooltips);

  Object.entries(ngieRowConfig)
    .filter(([, config]) => config.group === group)
    .forEach(([key, config]) => {
      addGroupDataSubheaderCells({
        list: rows,
        data: ngie,
        key,
        config,
        isImportTemplate,
      });
    });
  return rows;
};

const generateSheetBuilder = (isImportTemplate) => [
  ...getGroupDataInitialSheetBuilder(),
  {
    label: `Relevant Expense Amounts Recognised in the Group's P&L (s.411) (enter as positive)`,
    type: "header",
    headerType: "h1",
  },
  { label: "Loan Relationship Amounts", type: "header" },
  ...getTableRows(ngieGroups.rexpLoanRelationshipAmounts, isImportTemplate),
  { label: "Derivative Contract Amounts", type: "header" },
  ...getTableRows(ngieGroups.rexpDerivativeContractAmounts, isImportTemplate),
  { label: "Finance Leases and Other Financing Arrangements", type: "header" },
  ...getTableRows(
    ngieGroups.rexpFinanceLeasesAndOtherFinancingArrangements,
    isImportTemplate,
  ),
  { label: "Other Relevant Expense Amounts", type: "header" },
  ...getTableRows(ngieGroups.rexpOtherRelevantExpenseAmounts, isImportTemplate),
  // { label: '-', type: 'header' },
  {
    label: `Relevant Income Amounts Recognised in the Group's P&L (s.411) (enter as negative numbers)`,
    type: "header",
    headerType: "h1",
  },
  { label: "Loan Relationship Amounts", type: "header" },
  ...getTableRows(ngieGroups.rincLoanRelationshipAmounts, isImportTemplate),
  { label: "Derivative Contract Amounts", type: "header" },
  ...getTableRows(ngieGroups.rincDerivativeContractAmounts, isImportTemplate),
  { label: "Finance Leases and Other Financing Arrangements", type: "header" },
  ...getTableRows(
    ngieGroups.rincFinanceLeasesAndOtherFinancingArrangements,
    isImportTemplate,
  ),
  { label: "Other Relevant Income Amounts", type: "header" },
  ...getTableRows(ngieGroups.rincOtherRelevantIncomeAmounts, isImportTemplate),
  // { label: '-', type: 'header' },
  {
    label: "Other adjustments required to calculate NGIE",
    type: "header",
    headerType: "h1",
  },
  {
    label: "Automatic Application of Disregard Regulations at a Group Level",
    type: "header",
  },
  ...getTableRows(ngieGroups.oadjAutomaticApplication, isImportTemplate),
  {
    label:
      "Adjustments for the Interest Allowance (Non-Consolidated Investment) Election",
    type: "header",
  },
  ...getTableRows(
    ngieGroups.oadjAdjustmentsForInterestAllowanceNonConsolidated,
    isImportTemplate,
  ),
  {
    label:
      "Adjustments for the Interest Allowance (Consolidated Partnership) Election",
    type: "header",
  },
  ...getTableRows(
    ngieGroups.oadjAdjustmentsForInterestAllowanceConsolidated,
    isImportTemplate,
  ),
  {
    label:
      "Adjustments in respect of the interest allowance (alternative calculation) election (s423-426)",
    type: "header",
  },
  ...getTableRows(
    ngieGroups.oadjAdjustmentsForInterestAllowanceAlternativeCalculation,
    isImportTemplate,
  ),
  { label: "Other", type: "header" },
  ...getTableRows(ngieGroups.oadjOther, isImportTemplate),
];

const worksheetName = "NGIE";

export const handleNgieTemplateDownload = async ({
  workbook,
  isImportTemplate = true,
  isClientFacing = true,
}) => {
  const params = {
    sheetBuilder: generateSheetBuilder(isImportTemplate),
    documentName: "NGIE Import Template",
    worksheetName,
    isClientFacing,
  };
  if (workbook) {
    addGroupDataWorksheet({ ...params, workbook, isImportTemplate });
  } else {
    await downloadGroupDataImportTemplate(params);
  }
};

export const handleNgieCompImport = async ({
  file,
  setErrors,
  setWarnings,
  setNewData,
}) => {
  try {
    const worksheet = await getWorksheetFromFile(file, worksheetName);
    const sheetBuilder = generateSheetBuilder();
    const { results: ngie, warnings } = await extractKeyValuePairsFromExcel(
      worksheet,
      sheetBuilder,
    );

    // Validate ngie keys:
    validateImportedGroupData({ importedData: ngie, sheetBuilder, setErrors });

    if (warnings?.length > 0) {
      setNewData(ngie);
      setWarnings(warnings);
      return;
    }

    return ngie;
  } catch (error) {
    const errorObject = JSON.parse(error.message);
    console.error("Error importing NGIE:", errorObject ?? error);
    if (errorObject.missingWorkSheetErrorMessage) {
      setErrors([
        {
          title:
            errorObject.missingWorkSheetErrorMessage ?? "Error Importing NGIE",
        },
      ]);
    }
  }
};
